import { useEffect, useState } from "react";

// Screenshot / copy deterrents for the QP AI chat.
//
// A web page cannot truly stop screenshots - the OS (or a phone camera) takes
// them outside the browser. What this does is make the easy routes useless:
// - hides the content whenever the window loses focus or the tab is hidden
//   (Windows Snipping Tool / Win+Shift+S, app switching, screen-share pickers),
// - hides it on PrintScreen and the macOS Cmd+Shift+3/4/5 shortcuts, and
//   overwrites the clipboard after PrintScreen,
// - blocks selection, copy/cut and the context menu (callers apply the
//   handlers returned here), and printing (see .qpai-guard in index.css).
// Hidden content comes back when the viewer clicks the shield.
const isScreenshotCombo = (e) =>
  e.key === "PrintScreen" || (e.metaKey && e.shiftKey && ["3", "4", "5", "#", "$", "%"].includes(e.key));

const useScreenGuard = () => {
  const [shielded, setShielded] = useState(false);

  useEffect(() => {
    const shield = () => setShielded(true);
    const onVisibility = () => document.visibilityState === "hidden" && shield();
    const onKey = (e) => {
      if (!isScreenshotCombo(e)) return;
      shield();
      if (e.key === "PrintScreen") navigator.clipboard?.writeText("").catch(() => {});
    };
    window.addEventListener("blur", shield);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("keyup", onKey, true);
    return () => {
      window.removeEventListener("blur", shield);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("keyup", onKey, true);
    };
  }, []);

  const block = (e) => e.preventDefault();
  // Let the message box behave normally (typing, paste, its own copy).
  const unlessInput = (e) => {
    if (e.target.closest?.("input, textarea")) return;
    e.preventDefault();
  };

  return {
    shielded,
    resume: () => setShielded(false),
    guardProps: { onCopy: unlessInput, onCut: unlessInput, onContextMenu: block, onDragStart: block }
  };
};

export default useScreenGuard;
